import { Toaster } from "@/components/ui/toaster";
import { Toaster as Sonner } from "@/components/ui/sonner";
import { TooltipProvider } from "@/components/ui/tooltip";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { BrowserRouter, Routes, Route, Navigate } from "react-router-dom";
import { AuthProvider } from "@/contexts/AuthContext";
import { ThemeProvider } from "@/contexts/ThemeContext";
import { ProtectedRoute } from "@/components/ProtectedRoute";
import { RouteAwareUpdateBanner } from "@/components/RouteAwareUpdateBanner";

// Pages
import LoginPage from "@/pages/LoginPage";
import DashboardPage from "@/pages/DashboardPage";
import GymsPage from "@/pages/GymsPage";
import UsersPage from "@/pages/UsersPage";
import ClientsPage from "@/pages/ClientsPage";
import NutritionPage from "@/pages/NutritionPage";
import MembershipsPage from "@/pages/MembershipsPage";
import ScannerPage from "@/pages/ScannerPage";
import InventoryPage from "@/pages/InventoryPage";
import ClassesPage from "@/pages/ClassesPage";
import WebhooksPage from "@/pages/WebhooksPage";
import SettingsPage from "@/pages/SettingsPage";
import ReportsPage from "@/pages/ReportsPage";
import UnauthorizedPage from "@/pages/UnauthorizedPage";
import WhatsAppPage from "@/pages/WhatsAppPage";
import ResetPasswordPage from "@/pages/ResetPasswordPage";
import JoinGymPage from "@/pages/JoinGymPage";
import NutritionBookingPage from "@/pages/NutritionBookingPage";
import RegisterGymPage from "@/pages/RegisterGymPage";
import LandingPage from "@/pages/Index";
import SelfCheckInPage from "@/pages/SelfCheckInPage";
import NotFound from "@/pages/NotFound";
import ExpensesPage from "@/pages/ExpensesPage";
import SalesPage from "@/pages/SalesPage";
import LegalPage from "@/pages/LegalPage";
import SupportPage from "@/pages/SupportPage";
import EmployeesPage from "@/pages/EmployeesPage";
import UsagePage from "@/pages/UsagePage";
import RewardsPage from "@/pages/RewardsPage";
import FacturacionPage from "@/pages/FacturacionPage";

import { useAuth } from "@/contexts/AuthContext";
import { useEffect } from "react";
import { useLocation } from "react-router-dom";
import {
  initOneSignalSdk,
  isPushAdminRole,
  syncOneSignalUser,
} from "@/lib/onesignal";

const queryClient = new QueryClient();

// Public/check-in surfaces must never initialize OneSignal nor prompt visitors.
const PUSH_BLOCKED_PATHS = new Set([
  '/',
  '/login',
  '/register-gym',
  '/reset-password',
  '/unauthorized',
]);
const PUSH_BLOCKED_PREFIXES = ['/checkin/', '/join/', '/legal/', '/nutricion/'];

const isPushAllowedRoute = (pathname: string): boolean => {
  if (PUSH_BLOCKED_PATHS.has(pathname)) return false;
  return !PUSH_BLOCKED_PREFIXES.some((prefix) => pathname.startsWith(prefix));
};

const OneSignalManager = ({ children }: { children: React.ReactNode }) => {
  const { user } = useAuth();
  const { pathname } = useLocation();
  const isAllowedRoute = isPushAllowedRoute(pathname);
  const isAdmin = isPushAdminRole(user?.role);

  useEffect(() => {
    if (isAllowedRoute) initOneSignalSdk();
  }, [isAllowedRoute]);

  useEffect(() => {
    if (!isAllowedRoute) {
      syncOneSignalUser(null);
      return;
    }
    syncOneSignalUser(isAdmin ? user : null);
  }, [isAllowedRoute, isAdmin, user]);

  return <>{children}</>;
};

const App = () => {
  return (
    <QueryClientProvider client={queryClient}>
      <ThemeProvider>
        <AuthProvider>
          <TooltipProvider>
            <Toaster />
            <Sonner />
            <BrowserRouter future={{ v7_startTransition: true, v7_relativeSplatPath: true }}>
              <OneSignalManager>
                <Routes>
                  {/* Public routes */}
                  <Route path="/login" element={<LoginPage />} />
                  <Route path="/register-gym" element={<RegisterGymPage />} />
                  <Route path="/unauthorized" element={<UnauthorizedPage />} />
                  <Route path="/reset-password" element={<ResetPasswordPage />} />
                  <Route path="/join/:slug" element={<JoinGymPage />} />
                  <Route path="/nutricion/:slug" element={<NutritionBookingPage />} />

                  {/* Root route: Landing Page */}
                  <Route path="/" element={<LandingPage />} />
                  <Route path="/legal/:type" element={<LegalPage />} />
                  <Route path="/checkin/:slug" element={<SelfCheckInPage />} />

                  {/* Protected routes */}
                  <Route
                    path="/dashboard"
                    element={
                      <ProtectedRoute>
                        <DashboardPage />
                      </ProtectedRoute>
                    }
                  />

                {/* Super Admin only */}
                <Route
                  path="/gyms"
                  element={
                    <ProtectedRoute allowedRoles={['super_admin']}>
                      <GymsPage />
                    </ProtectedRoute>
                  }
                />

                {/* Admin routes */}
                <Route
                  path="/users"
                  element={
                    <ProtectedRoute allowedRoles={['super_admin', 'gym_admin']}>
                      <UsersPage />
                    </ProtectedRoute>
                  }
                />

                {/* Employee Management */}
                <Route
                  path="/employees"
                  element={
                    <ProtectedRoute
                      allowedRoles={['gym_admin', 'gym_supervisor', 'gym_staff', 'staff']}
                      requiredModule="employees"
                    >
                      <EmployeesPage />
                    </ProtectedRoute>
                  }
                />

                {/* Gym routes */}
                <Route
                  path="/clients"
                  element={
                    <ProtectedRoute
                      allowedRoles={['super_admin', 'gym_admin', 'gym_supervisor', 'gym_staff', 'staff']}
                      requiredModule="clients"
                    >
                      <ClientsPage />
                    </ProtectedRoute>
                  }
                />

                {/* Nutrición (add-on opcional) */}
                <Route
                  path="/nutrition"
                  element={
                    <ProtectedRoute
                      allowedRoles={['super_admin', 'gym_admin', 'gym_supervisor', 'gym_staff', 'staff']}
                      requiredModule="nutricion"
                    >
                      <NutritionPage />
                    </ProtectedRoute>
                  }
                />

                <Route
                  path="/memberships"
                  element={
                    <ProtectedRoute
                      allowedRoles={['super_admin', 'gym_admin', 'gym_supervisor', 'gym_staff', 'staff']}
                      requiredModule="memberships"
                    >
                      <MembershipsPage />
                    </ProtectedRoute>
                  }
                />

                <Route
                  path="/scanner"
                  element={
                    <ProtectedRoute
                      allowedRoles={['super_admin', 'gym_admin', 'gym_supervisor', 'gym_staff', 'staff']}
                      requiredModule="scanner"
                    >
                      <ScannerPage />
                    </ProtectedRoute>
                  }
                />

                <Route
                  path="/inventory"
                  element={
                    <ProtectedRoute
                      allowedRoles={['super_admin', 'gym_admin', 'gym_supervisor']}
                      requiredModule="inventory"
                    >
                      <InventoryPage />
                    </ProtectedRoute>
                  }
                />

                <Route
                  path="/sales"
                  element={
                    <ProtectedRoute
                      allowedRoles={['super_admin', 'gym_admin', 'gym_supervisor', 'gym_staff', 'staff']}
                      requiredModule="sales"
                    >
                      <SalesPage />
                    </ProtectedRoute>
                  }
                />

                <Route
                  path="/expenses"
                  element={
                    <ProtectedRoute
                      allowedRoles={['super_admin', 'gym_admin', 'gym_supervisor']}
                      requiredModule="expenses"
                    >
                      <ExpensesPage />
                    </ProtectedRoute>
                  }
                />

                <Route
                  path="/rewards"
                  element={
                    <ProtectedRoute
                      allowedRoles={['super_admin', 'gym_admin', 'gym_supervisor']}
                      requiredModule="rewards"
                    >
                      <RewardsPage />
                    </ProtectedRoute>
                  }
                />

                <Route
                  path="/facturacion"
                  element={
                    <ProtectedRoute
                      allowedRoles={['super_admin', 'gym_admin', 'gym_supervisor']}
                      requiredModule="facturacion"
                    >
                      <FacturacionPage />
                    </ProtectedRoute>
                  }
                />

                <Route
                  path="/classes"
                  element={
                    <ProtectedRoute
                      allowedRoles={['super_admin', 'gym_admin', 'gym_supervisor', 'gym_staff', 'staff']}
                      requiredModule="classes"
                    >
                      <ClassesPage />
                    </ProtectedRoute>
                  }
                />

                <Route
                  path="/settings"
                  element={
                    <ProtectedRoute allowedRoles={['super_admin', 'gym_admin']}>
                      <SettingsPage />
                    </ProtectedRoute>
                  }
                />

                <Route
                  path="/reports"
                  element={
                    <ProtectedRoute
                      allowedRoles={['super_admin', 'gym_admin', 'gym_supervisor']}
                      requiredModule="reports"
                    >
                      <ReportsPage />
                    </ProtectedRoute>
                  }
                />

                <Route
                  path="/whatsapp"
                  element={
                    <ProtectedRoute
                      allowedRoles={['super_admin', 'gym_admin', 'gym_staff', 'staff']}
                    >
                      <WhatsAppPage />
                    </ProtectedRoute>
                  }
                />

                <Route
                  path="/webhooks"
                  element={
                    <ProtectedRoute allowedRoles={['super_admin']}>
                      <WebhooksPage />
                    </ProtectedRoute>
                  }
                />

                <Route
                  path="/usage"
                  element={
                    <ProtectedRoute allowedRoles={['super_admin']}>
                      <UsagePage />
                    </ProtectedRoute>
                  }
                />

                <Route
                  path="/support"
                  element={
                    <ProtectedRoute allowedRoles={['super_admin', 'gym_admin', 'gym_supervisor', 'gym_staff', 'staff']}>
                      <SupportPage />
                    </ProtectedRoute>
                  }
                />

                {/* Catch all */}
                <Route path="*" element={<NotFound />} />
              </Routes>
              <RouteAwareUpdateBanner />
              </OneSignalManager>
            </BrowserRouter>
          </TooltipProvider>
        </AuthProvider>
      </ThemeProvider>
    </QueryClientProvider>
  );
};

export default App;
